import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useTheme } from '@mui/material';
import { DndContext, DragOverlay } from '@dnd-kit/core';

import { COL_LETTERS, RACK_COLS, RACK_THEME } from '../../constants';
import { RackCell, RackDragOverlay } from '..';

import type {
  CollisionDetection,
  DragEndEvent,
  DragStartEvent,
  SensorDescriptor
} from '@dnd-kit/core';
import type { RackCell as RackCellType } from '../../types';

type Props = {
  grid: RackCellType[][];
  flatCells: RackCellType[];
  onCellClick: (cell: RackCellType) => void;
  selectedCell: number | null;
  activeId: number | null;
  sensors: SensorDescriptor<object>[];
  collisionDetection: CollisionDetection;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  isReadOnly?: boolean;
};

const DesktopRackGrid: React.FC<Props> = ({
  grid,
  flatCells,
  onCellClick,
  selectedCell,
  activeId,
  sensors,
  collisionDetection,
  onDragStart,
  onDragEnd,
  isReadOnly = false
}) => {
  const { t } = useTranslation('workOrderDetails');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const activeCell = activeId
    ? flatCells.find(c => c.sequenceNumber === activeId) ?? null
    : null;

  const labelSx = {
    fontFamily: 'monospace',
    color: isDarkMode
      ? RACK_THEME.labelColor.dark
      : RACK_THEME.labelColor.light,
    fontSize: '0.75rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const handleCellClick = useCallback(
    (cell: RackCellType) => {
      onCellClick(cell);
    },
    [onCellClick]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <Box
        sx={{
          backgroundColor: isDarkMode
            ? RACK_THEME.frameBg.dark
            : RACK_THEME.frameBg.light,
          border: `2px solid ${isDarkMode ? RACK_THEME.grooveLine.dark : RACK_THEME.grooveLine.light}`,
          borderRadius: '8px',
          p: 1.5,
          display: 'grid',
          gridTemplateColumns: '48px repeat(4, 1fr)',
          gridTemplateRows: 'auto repeat(5, 1fr)',
          gap: '6px'
        }}
      >
        <Box />

        {Array.from({ length: RACK_COLS }, (_, i) => (
          <Typography key={`col-${i}`} sx={labelSx}>
            {t('rack.columnHeader', { letter: COL_LETTERS[i] ?? '' })}
          </Typography>
        ))}

        {grid.map((row, rowIdx) => (
          <React.Fragment key={`row-${rowIdx}`}>
            <Typography sx={labelSx}>
              {t('rack.rowHeader', { number: rowIdx + 1 })}
            </Typography>

            {row.map(cell => (
              <RackCell
                key={cell.sequenceNumber}
                cell={cell}
                isSelected={selectedCell === cell.sequenceNumber}
                onClick={handleCellClick}
                isReadOnly={isReadOnly}
              />
            ))}
          </React.Fragment>
        ))}
      </Box>

      <DragOverlay dropAnimation={null}>
        {activeCell && <RackDragOverlay cell={activeCell} />}
      </DragOverlay>
    </DndContext>
  );
};

export default DesktopRackGrid;
